import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { QRCodeSVG } from 'qrcode.react';
import {
  QrCode,
  Download,
  Copy,
  Share2,
  Globe,
  Check,
  Lock,
  Smartphone,
  Link as LinkIcon
} from 'lucide-react';
import toast from 'react-hot-toast';

const UserKit = () => {
  const { user } = useAuth();
  const { hasFeature } = useSubscription();
  const [websites, setWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [qrSize, setQrSize] = useState(256);
  const [qrColor, setQrColor] = useState('#000000');
  const [qrBgColor, setQrBgColor] = useState('#FFFFFF');
  const [includeLogo, setIncludeLogo] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchWebsites();
    }
  }, [user]);

  const fetchWebsites = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'websites'),
        where('userId', '==', user.uid)
      );
      const snapshot = await getDocs(q);
      const websitesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setWebsites(websitesList);
      if (websitesList.length > 0) {
        setSelectedWebsite(websitesList[0]);
      }
    } catch (error) {
      console.error('Error fetching websites:', error);
    } finally {
      setLoading(false);
    }
  };

  const getWebsiteUrl = (website) => {
    if (website?.domain) {
      return `https://${website.domain}`;
    }
    return `https://hostbit.cloud/site/${website?.id}`;
  };

  const handleDownload = () => {
    const canvas = document.querySelector('canvas');
    if (canvas) {
      const link = document.createElement('a');
      link.download = `qr-code-${selectedWebsite?.name || 'website'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('QR code downloaded!');
    }
  };

  const handleCopy = () => {
    const url = getWebsiteUrl(selectedWebsite);
    navigator.clipboard.writeText(url);
    toast.success('URL copied to clipboard!');
  };

  const handleShare = async () => {
    const url = getWebsiteUrl(selectedWebsite);
    if (navigator.share) {
      try {
        await navigator.share({
          title: selectedWebsite?.name || 'My Website',
          url: url
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      handleCopy();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">User Kit</h1>
        <p className="text-gray-500">Generate QR codes and share your websites</p>
      </div>

      {websites.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-dashed border-gray-300">
          <QrCode className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No websites yet</h3>
          <p className="text-gray-500 mb-4">Create a website first to generate QR codes</p>
          <button
            onClick={() => window.location.href = '/site/wizard'}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
          >
            Create Website
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* QR Code Preview */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="flex flex-col items-center">
              {/* Website Selector */}
              <div className="w-full max-w-md mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Website
                </label>
                <select
                  value={selectedWebsite?.id}
                  onChange={(e) => {
                    const website = websites.find(w => w.id === e.target.value);
                    setSelectedWebsite(website);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {websites.map(website => (
                    <option key={website.id} value={website.id}>
                      {website.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* QR Code */}
              <div className="p-8 bg-white rounded-2xl shadow-lg border-2 border-dashed border-gray-200">
                <QRCodeSVG
                  value={getWebsiteUrl(selectedWebsite)}
                  size={qrSize}
                  level="H"
                  bgColor={qrBgColor}
                  fgColor={qrColor}
                  includeMargin={true}
                  imageSettings={includeLogo ? {
                    src: '/favicon.svg',
                    height: qrSize * 0.2,
                    width: qrSize * 0.2,
                    excavate: true,
                  } : undefined}
                />
              </div>

              {/* Website URL */}
              <div className="mt-6 flex items-center gap-2 text-gray-600">
                <LinkIcon className="w-4 h-4" />
                <span className="text-sm">{getWebsiteUrl(selectedWebsite)}</span>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
                >
                  <Download className="w-5 h-5" />
                  Download
                </button>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Copy className="w-5 h-5" />
                  Copy URL
                </button>
                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Share2 className="w-5 h-5" />
                  Share
                </button>
              </div>
            </div>
          </div>

          {/* Customization */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Customize QR Code</h3>
            
            <div className="space-y-6">
              {/* Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Size: {qrSize}px
                </label>
                <input
                  type="range"
                  min="128"
                  max="512"
                  step="64"
                  value={qrSize}
                  onChange={(e) => setQrSize(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              {/* QR Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  QR Code Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={qrColor}
                    onChange={(e) => setQrColor(e.target.value)}
                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                  />
                  <span className="text-sm text-gray-600">{qrColor}</span>
                </div>
              </div>

              {/* Background Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Background Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={qrBgColor}
                    onChange={(e) => setQrBgColor(e.target.value)}
                    className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                  />
                  <span className="text-sm text-gray-600">{qrBgColor}</span>
                </div>
              </div>

              {/* Include Logo */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="includeLogo"
                  checked={includeLogo}
                  onChange={(e) => setIncludeLogo(e.target.checked)}
                  className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                />
                <label htmlFor="includeLogo" className="text-sm text-gray-700">
                  Include logo in center
                </label>
              </div>
            </div>

            {/* Tips */}
            <div className="mt-8 p-4 bg-blue-50 rounded-xl">
              <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                <Smartphone className="w-4 h-4" />
                Pro Tips
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Use high contrast colors for better scanning</li>
                <li>• Test the QR code before printing</li>
                <li>• Download in PNG for best quality</li>
                <li>• Keep the QR code at least 2cm x 2cm when printing</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserKit;
